import { html, createSignal, css } from 'onefold';
import { Playground } from '../../components/Playground';

const examples = [
  { title: 'Counter', code: `import { createSignal, html, mount } from 'onefold';

function Counter() {
  const count = createSignal(0);

  return html\`
    <div>
      <h2>Count: \${() => count()}</h2>
      <button onclick=\${() => count.set(n => n - 1)}>-</button>
      <button onclick=\${() => count.set(n => n + 1)}>+</button>
      <button onclick=\${() => count.set(0)}>Reset</button>
    </div>
  \`;
}

mount(Counter(), document.getElementById('app'));` },

  { title: 'Todo List', code: `import { createSignal, html, mount } from 'onefold';

function App() {
  const todos = createSignal([]);
  const input = createSignal('');

  const add = () => {
    if (input().trim()) {
      todos.set(t => [...t, { id: Date.now(), text: input(), done: false }]);
      input.set('');
    }
  };

  const toggle = (id) => {
    todos.set(t => t.map(todo => todo.id === id ? { ...todo, done: !todo.done } : todo));
  };

  const remove = (id) => {
    todos.set(t => t.filter(todo => todo.id !== id));
  };

  return html\`
    <div>
      <h3>Todo List</h3>
      <div style="display:flex;gap:8px;margin-bottom:12px">
        <input value=\${() => input()} oninput=\${(e) => input.set(e.target.value)} onkeydown=\${(e) => e.key === 'Enter' && add()} placeholder="Add a task..." style="flex:1" />
        <button onclick=\${add}>Add</button>
      </div>
      <ul style="list-style:none;padding:0">
        \${() => todos().map(todo => html\`
          <li style="display:flex;align-items:center;gap:8px;padding:6px 0;border-bottom:1px solid #f1f5f9">
            <input type="checkbox" onchange=\${() => toggle(todo.id)} />
            <span style=\${todo.done ? 'text-decoration:line-through;color:#94a3b8' : ''}>\${todo.text}</span>
            <button onclick=\${() => remove(todo.id)} style="margin-left:auto;font-size:11px">✕</button>
          </li>
        \`)}
      </ul>
      <p style="font-size:12px;color:#666">\${() => todos().filter(t => !t.done).length} remaining</p>
    </div>
  \`;
}

mount(App(), document.getElementById('app'));` },

  { title: 'Theme Toggle', code: `import { createSignal, html, mount } from 'onefold';
import { createTheme } from 'onefold/theme';

function App() {
  const theme = createTheme({
    light: { bg: '#ffffff', text: '#1a1a1a', primary: '#4f46e5', card: '#f8fafc', border: '#e5e7eb' },
    dark:  { bg: '#0f172a', text: '#e2e8f0', primary: '#818cf8', card: '#1e293b', border: '#334155' },
  }, 'light');

  return html\`
    <div style="padding:16px;border-radius:8px;background:var(--bg);color:var(--text);transition:all 0.3s">
      <h3 style="color:var(--primary)">\${() => theme.current() === 'dark' ? 'Dark Mode' : 'Light Mode'}</h3>
      <div style="margin:12px 0;padding:12px;background:var(--card);border:1px solid var(--border);border-radius:6px">
        <p style="font-size:13px">Card using CSS variables from createTheme.</p>
      </div>
      <button onclick=\${() => theme.toggle()}>\${() => theme.current() === 'dark' ? 'Switch to Light' : 'Switch to Dark'}</button>
    </div>
  \`;
}

mount(App(), document.getElementById('app'));` },

  { title: 'Shopping Cart', code: `import { createSignal, createStore, html, mount } from 'onefold';

function App() {
  const cart = createStore({ items: [], total: 0 });

  const products = [
    { id: 1, name: 'Headphones', price: 79.99, emoji: '🎧' },
    { id: 2, name: 'Keyboard', price: 129.99, emoji: '⌨️' },
    { id: 3, name: 'USB-C Hub', price: 49.99, emoji: '🔌' },
    { id: 4, name: 'Webcam', price: 59.99, emoji: '📷' },
  ];

  function addToCart(p) {
    const items = cart().items;
    const existing = items.find(i => i.id === p.id);
    if (existing) {
      cart.update({ items: items.map(i => i.id === p.id ? { ...i, qty: i.qty + 1 } : i) });
    } else {
      cart.update({ items: [...items, { ...p, qty: 1 }] });
    }
    cart.update({ total: cart().items.reduce((s, i) => s + i.price * i.qty, 0) });
  }

  function removeFromCart(id) {
    cart.update({ items: cart().items.filter(i => i.id !== id) });
    cart.update({ total: cart().items.reduce((s, i) => s + i.price * i.qty, 0) });
  }

  return html\`
    <div>
      <h3>Shop</h3>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-bottom:16px">
        \${products.map(p => html\`
          <div style="padding:8px;border:1px solid #e5e7eb;border-radius:6px;display:flex;align-items:center;gap:6px">
            <span style="font-size:20px">\${p.emoji}</span>
            <div style="flex:1"><div style="font-size:12px;font-weight:600">\${p.name}</div><div style="font-size:11px;color:#666">$\${p.price}</div></div>
            <button onclick=\${() => addToCart(p)} style="font-size:10px;padding:3px 6px">+</button>
          </div>
        \`)}
      </div>
      <div style="border-top:1px solid #e5e7eb;padding-top:12px">
        <h4>Cart (\${() => cart().items.length})</h4>
        \${() => cart().items.length === 0 ? html\`<p style="color:#94a3b8;font-size:12px">Empty</p>\` : html\`<div>
          \${() => cart().items.map(i => html\`<div style="display:flex;align-items:center;gap:6px;padding:4px 0;font-size:12px">\${i.emoji} \${i.name} x\${i.qty} <span style="margin-left:auto">$\${(i.price*i.qty).toFixed(2)}</span><button onclick=\${() => removeFromCart(i.id)} style="font-size:10px;padding:1px 4px;color:#ef4444">✕</button></div>\`)}
          <div style="margin-top:8px;font-weight:700;text-align:right">Total: $\${() => cart().total.toFixed(2)}</div>
        </div>\`}
      </div>
    </div>
  \`;
}

mount(App(), document.getElementById('app'));` },

  { title: 'Live Search', code: `import { createSignal, html, mount } from 'onefold';
import { debounce } from 'onefold/utils';

function App() {
  const query = createSignal('');
  const results = createSignal([]);
  const loading = createSignal(false);

  const countries = ['Argentina','Australia','Brazil','Canada','China','Denmark','Egypt','Finland','France','Germany','India','Italy','Japan','Kenya','Mexico','Netherlands','Norway','Peru','Russia','Spain','Sweden','Thailand','United Kingdom','United States','Vietnam'];

  const search = debounce((q) => {
    if (!q.trim()) { results.set([]); loading.set(false); return; }
    results.set(countries.filter(c => c.toLowerCase().includes(q.toLowerCase())));
    loading.set(false);
  }, 300);

  return html\`
    <div>
      <h3>Country Search (debounced)</h3>
      <input oninput=\${(e) => { query.set(e.target.value); loading.set(true); search(e.target.value); }} placeholder="Type a country..." style="width:100%;margin-bottom:12px" />
      \${() => loading() ? html\`<p style="font-size:12px;color:#666">Searching...</p>\` : null}
      \${() => !query() ? html\`<p style="font-size:12px;color:#94a3b8">\${countries.length} countries</p>\` : null}
      \${() => query() && !loading() && results().length === 0 ? html\`<p style="font-size:13px;color:#ef4444">No results</p>\` : null}
      \${() => results().length > 0 ? html\`<ul style="list-style:none;padding:0;max-height:180px;overflow-y:auto">\${results().map(c => html\`<li style="padding:6px 10px;border-bottom:1px solid #f1f5f9;font-size:13px">\${c}</li>\`)}</ul>\` : null}
    </div>
  \`;
}

mount(App(), document.getElementById('app'));` },

  { title: 'Tabs Component', code: `import { createSignal, html, mount } from 'onefold';

function Tabs({ tabs }) {
  const active = createSignal(0);
  return html\`
    <div>
      <div style="display:flex;border-bottom:2px solid #e5e7eb;margin-bottom:12px">
        \${tabs.map((tab, i) => html\`
          <button onclick=\${() => active.set(i)} style=\${() => 'padding:8px 16px;border:none;background:none;cursor:pointer;font-size:13px;font-weight:500;border-bottom:2px solid ' + (active() === i ? '#4f46e5;color:#4f46e5;margin-bottom:-2px' : 'transparent;color:#64748b')}>\${tab.label}</button>
        \`)}
      </div>
      \${() => tabs[active()].content()}
    </div>
  \`;
}

function App() {
  return Tabs({ tabs: [
    { label: 'Profile', content: () => html\`<div><h4>Profile</h4><p style="font-size:13px;color:#666">Name: Jane Doe<br>Role: Engineer<br>Joined: 2023</p></div>\` },
    { label: 'Settings', content: () => html\`<div><h4>Settings</h4><p style="font-size:13px;color:#666">Notifications: On<br>Language: English<br>Timezone: UTC-5</p></div>\` },
    { label: 'Activity', content: () => html\`<div><h4>Activity</h4><ul style="font-size:13px;color:#666"><li>Pushed 3 commits</li><li>Reviewed PR #142</li><li>Closed issue #89</li></ul></div>\` },
  ]});
}

mount(App(), document.getElementById('app'));` },

  { title: 'Form Validation', code: `import { html, mount } from 'onefold';
import { createForm, required, email, minLength } from 'onefold/form';

function App() {
  const form = createForm({
    name: { initial: '', rules: [required('Name required'), minLength(2, 'Min 2 chars')] },
    email: { initial: '', rules: [required('Email required'), email('Invalid email')] },
    password: { initial: '', rules: [required('Password required'), minLength(6, 'Min 6 chars')] },
  });

  return html\`
    <div>
      <h3>Registration</h3>
      <form onsubmit=\${(e) => { e.preventDefault(); form.submit((v) => alert('Submitted: ' + JSON.stringify(v))); }}>
        <div style="margin-bottom:10px"><label style="font-size:12px;font-weight:600;display:block">Name</label><input oninput=\${form.fields.name.handle} style="width:100%" />\${() => form.fields.name.error() ? html\`<p style="color:#ef4444;font-size:11px;margin-top:2px">\${form.fields.name.error()}</p>\` : null}</div>
        <div style="margin-bottom:10px"><label style="font-size:12px;font-weight:600;display:block">Email</label><input type="email" oninput=\${form.fields.email.handle} style="width:100%" />\${() => form.fields.email.error() ? html\`<p style="color:#ef4444;font-size:11px;margin-top:2px">\${form.fields.email.error()}</p>\` : null}</div>
        <div style="margin-bottom:10px"><label style="font-size:12px;font-weight:600;display:block">Password</label><input type="password" oninput=\${form.fields.password.handle} style="width:100%" />\${() => form.fields.password.error() ? html\`<p style="color:#ef4444;font-size:11px;margin-top:2px">\${form.fields.password.error()}</p>\` : null}</div>
        <div style="display:flex;gap:8px;align-items:center"><button type="submit">Register</button><button type="button" onclick=\${() => form.reset()}>Reset</button><span style="font-size:11px;color:#666;margin-left:auto">\${() => form.valid() ? '✓' : '✗'} \${() => form.dirty() ? 'modified' : 'pristine'}</span></div>
      </form>
    </div>
  \`;
}

mount(App(), document.getElementById('app'));` },

  { title: 'Data Fetching', code: `import { createSignal, createResource, html, mount } from 'onefold';

function App() {
  const userId = createSignal(1);
  const user = createResource(userId, async (id) => {
    const res = await fetch('https://jsonplaceholder.typicode.com/users/' + id);
    return res.json();
  });

  return html\`
    <div>
      <h3>User Profile</h3>
      \${() => {
        if (user.loading()) return html\`<p style="color:#666">Loading...</p>\`;
        if (user.error()) return html\`<p style="color:#ef4444">Error</p>\`;
        const d = user.data();
        if (!d) return null;
        return html\`<div style="padding:12px;border:1px solid #e5e7eb;border-radius:8px">
          <div style="font-weight:600">\${d.name}</div>
          <div style="font-size:12px;color:#666">\${d.email}</div>
          <div style="font-size:12px;color:#666">\${d.company.name}</div>
        </div>\`;
      }}
      <div style="display:flex;gap:8px;margin-top:12px">
        <button onclick=\${() => userId.set(n => Math.max(1, n - 1))}>← Prev</button>
        <span style="font-size:13px;padding:6px">ID: \${() => userId()}</span>
        <button onclick=\${() => userId.set(n => Math.min(10, n + 1))}>Next →</button>
      </div>
    </div>
  \`;
}

mount(App(), document.getElementById('app'));` },

  { title: 'Stopwatch', code: `import { createSignal, html, mount } from 'onefold';

function App() {
  const time = createSignal(0);
  const running = createSignal(false);
  let interval = null;

  function start() {
    if (running()) return;
    running.set(true);
    interval = setInterval(() => time.set(t => t + 10), 10);
  }

  function stop() {
    running.set(false);
    clearInterval(interval);
  }

  function reset() {
    stop();
    time.set(0);
  }

  function format(ms) {
    const mins = Math.floor(ms / 60000);
    const secs = Math.floor((ms % 60000) / 1000);
    const cents = Math.floor((ms % 1000) / 10);
    return String(mins).padStart(2, '0') + ':' + String(secs).padStart(2, '0') + '.' + String(cents).padStart(2, '0');
  }

  return html\`
    <div style="text-align:center">
      <h3>Stopwatch</h3>
      <div style="font-size:48px;font-family:monospace;font-weight:700;margin:20px 0">\${() => format(time())}</div>
      <div style="display:flex;gap:8px;justify-content:center">
        <button onclick=\${start} style=\${() => running() ? 'opacity:0.5' : ''}>Start</button>
        <button onclick=\${stop}>Stop</button>
        <button onclick=\${reset}>Reset</button>
      </div>
    </div>
  \`;
}

mount(App(), document.getElementById('app'));` },

  { title: 'Accordion', code: `import { createSignal, html, mount } from 'onefold';

function Accordion({ items }) {
  const openIndex = createSignal(-1);

  return html\`
    <div>
      \${items.map((item, i) => html\`
        <div style="border:1px solid #e5e7eb;border-radius:8px;margin-bottom:8px;overflow:hidden">
          <button
            onclick=\${() => openIndex.set(openIndex() === i ? -1 : i)}
            style="width:100%;padding:12px 16px;border:none;background:#f8fafc;cursor:pointer;display:flex;justify-content:space-between;align-items:center;font-size:14px;font-weight:500"
          >
            \${item.title}
            <span>\${() => openIndex() === i ? '−' : '+'}</span>
          </button>
          \${() => openIndex() === i ? html\`
            <div style="padding:12px 16px;font-size:13px;color:#666;border-top:1px solid #e5e7eb">\${item.content}</div>
          \` : null}
        </div>
      \`)}
    </div>
  \`;
}

function App() {
  return html\`
    <div>
      <h3>FAQ</h3>
      \${Accordion({ items: [
        { title: 'What is onefold?', content: 'A reactive UI framework with fine-grained signals, real DOM rendering, and zero dependencies.' },
        { title: 'Do I need a compiler?', content: 'No. The html tagged template works at runtime. No JSX, no Babel, no build step required.' },
        { title: 'How big is it?', content: 'Under 6kb gzipped for the core. Full framework with all features is ~16kb gzipped.' },
        { title: 'Is it production-ready?', content: 'Yes. TypeScript strict mode, full test coverage, secure by default. Used in production apps.' },
      ]})}
    </div>
  \`;
}

mount(App(), document.getElementById('app'));` },

  { title: 'Calculator', code: `import { createSignal, html, mount } from 'onefold';

function App() {
  const display = createSignal('0');
  const memory = createSignal(null);
  const op = createSignal(null);
  const fresh = createSignal(true);

  function input(n) {
    if (fresh()) { display.set(String(n)); fresh.set(false); }
    else { display.set(display() === '0' ? String(n) : display() + n); }
  }

  function operate(next) {
    if (memory() !== null && op()) {
      const a = memory(), b = parseFloat(display());
      const result = op() === '+' ? a + b : op() === '-' ? a - b : op() === '*' ? a * b : a / b;
      display.set(String(Math.round(result * 1e10) / 1e10));
    }
    memory.set(parseFloat(display()));
    op.set(next);
    fresh.set(true);
  }

  function equals() {
    operate(null);
    memory.set(null);
    fresh.set(true);
  }

  function clear() { display.set('0'); memory.set(null); op.set(null); fresh.set(true); }

  const btn = (label, action, style) => html\`<button onclick=\${action} style=\${'padding:12px;font-size:16px;border:1px solid #e5e7eb;border-radius:6px;cursor:pointer;' + (style || '')}>\${label}</button>\`;

  return html\`
    <div style="max-width:260px">
      <h3>Calculator</h3>
      <div style="padding:16px;background:#1e293b;color:white;border-radius:8px 8px 0 0;font-size:28px;font-family:monospace;text-align:right;min-height:50px">\${() => display()}</div>
      <div style="display:grid;grid-template-columns:repeat(4,1fr);gap:4px;padding:8px;background:#f1f5f9;border-radius:0 0 8px 8px">
        \${btn('C', clear, 'background:#fca5a5')} \${btn('±', () => display.set(String(-parseFloat(display()))))} \${btn('%', () => display.set(String(parseFloat(display())/100)))} \${btn('÷', () => operate('/'), 'background:#bfdbfe')}
        \${btn('7', () => input(7))} \${btn('8', () => input(8))} \${btn('9', () => input(9))} \${btn('×', () => operate('*'), 'background:#bfdbfe')}
        \${btn('4', () => input(4))} \${btn('5', () => input(5))} \${btn('6', () => input(6))} \${btn('-', () => operate('-'), 'background:#bfdbfe')}
        \${btn('1', () => input(1))} \${btn('2', () => input(2))} \${btn('3', () => input(3))} \${btn('+', () => operate('+'), 'background:#bfdbfe')}
        \${btn('0', () => input(0), 'grid-column:span 2')} \${btn('.', () => { if (!display().includes('.')) display.set(display() + '.'); fresh.set(false); })} \${btn('=', equals, 'background:#86efac')}
      </div>
    </div>
  \`;
}

mount(App(), document.getElementById('app'));` },

  { title: 'Pomodoro Timer', code: `import { createSignal, html, mount } from 'onefold';

function App() {
  const timeLeft = createSignal(25 * 60);
  const running = createSignal(false);
  const mode = createSignal('work');
  let interval = null;

  function format(s) {
    return String(Math.floor(s / 60)).padStart(2, '0') + ':' + String(s % 60).padStart(2, '0');
  }

  function start() {
    if (running()) return;
    running.set(true);
    interval = setInterval(() => {
      timeLeft.set(t => {
        if (t <= 0) { stop(); switchMode(); return 0; }
        return t - 1;
      });
    }, 1000);
  }

  function stop() { running.set(false); clearInterval(interval); }

  function switchMode() {
    const next = mode() === 'work' ? 'break' : 'work';
    mode.set(next);
    timeLeft.set(next === 'work' ? 25 * 60 : 5 * 60);
  }

  function reset() { stop(); timeLeft.set(mode() === 'work' ? 25 * 60 : 5 * 60); }

  return html\`
    <div style="text-align:center">
      <h3>Pomodoro</h3>
      <div style=\${() => 'display:inline-block;width:160px;height:160px;border-radius:50%;border:6px solid ' + (mode() === 'work' ? '#ef4444' : '#10b981') + ';display:flex;align-items:center;justify-content:center;margin:16px auto'}>
        <span style="font-size:36px;font-family:monospace;font-weight:700">\${() => format(timeLeft())}</span>
      </div>
      <p style="font-size:14px;font-weight:600;text-transform:uppercase;margin-bottom:12px;color:\${() => mode() === 'work' ? '#ef4444' : '#10b981'}">\${() => mode()}</p>
      <div style="display:flex;gap:8px;justify-content:center">
        <button onclick=\${start}>\${() => running() ? 'Running...' : 'Start'}</button>
        <button onclick=\${stop}>Pause</button>
        <button onclick=\${reset}>Reset</button>
        <button onclick=\${switchMode}>Skip</button>
      </div>
    </div>
  \`;
}

mount(App(), document.getElementById('app'));` },

  { title: 'Kanban Board', code: `import { createSignal, html, mount } from 'onefold';

function App() {
  const tasks = createSignal([
    { id: 1, text: 'Design homepage', col: 'todo' },
    { id: 2, text: 'Setup CI/CD', col: 'todo' },
    { id: 3, text: 'Write tests', col: 'doing' },
    { id: 4, text: 'Deploy v1', col: 'doing' },
    { id: 5, text: 'User research', col: 'done' },
  ]);

  function move(id, to) {
    tasks.set(prev => prev.map(t => t.id === id ? { ...t, col: to } : t));
  }

  function addTask() {
    const text = prompt('Task name:');
    if (text) tasks.set(prev => [...prev, { id: Date.now(), text, col: 'todo' }]);
  }

  function Column(name, color) {
    return html\`
      <div style="flex:1;min-width:140px">
        <h4 style="font-size:12px;text-transform:uppercase;color:\${color};margin-bottom:8px;letter-spacing:0.05em">\${name} (\${() => tasks().filter(t => t.col === name).length})</h4>
        <div style="min-height:100px;background:#f8fafc;border-radius:8px;padding:8px">
          \${() => tasks().filter(t => t.col === name).map(t => html\`
            <div style="background:white;border:1px solid #e5e7eb;border-radius:6px;padding:8px;margin-bottom:6px;font-size:12px">
              \${t.text}
              <div style="margin-top:6px;display:flex;gap:4px">
                \${name !== 'todo' ? html\`<button onclick=\${() => move(t.id, 'todo')} style="font-size:10px;padding:2px 4px">← Todo</button>\` : null}
                \${name !== 'doing' ? html\`<button onclick=\${() => move(t.id, 'doing')} style="font-size:10px;padding:2px 4px">Doing</button>\` : null}
                \${name !== 'done' ? html\`<button onclick=\${() => move(t.id, 'done')} style="font-size:10px;padding:2px 4px">Done →</button>\` : null}
              </div>
            </div>
          \`)}
        </div>
      </div>
    \`;
  }

  return html\`
    <div>
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:12px">
        <h3>Kanban Board</h3>
        <button onclick=\${addTask} style="font-size:12px">+ Add Task</button>
      </div>
      <div style="display:flex;gap:12px">
        \${Column('todo', '#f59e0b')}
        \${Column('doing', '#3b82f6')}
        \${Column('done', '#10b981')}
      </div>
    </div>
  \`;
}

mount(App(), document.getElementById('app'));` },

  { title: 'Color Palette', code: `import { createSignal, html, mount } from 'onefold';

function App() {
  const hue = createSignal(220);
  const saturation = createSignal(70);
  const lightness = createSignal(50);
  const count = createSignal(7);

  function generatePalette() {
    const h = hue(), s = saturation(), n = count();
    return Array.from({ length: n }, (_, i) => {
      const l = 95 - (i * (80 / (n - 1)));
      return { color: 'hsl(' + h + ',' + s + '%,' + l + '%)', label: Math.round(l) + '%' };
    });
  }

  return html\`
    <div>
      <h3>Color Palette Generator</h3>
      <div style="display:grid;gap:10px;margin-bottom:16px">
        <label style="font-size:12px;display:flex;align-items:center;gap:8px">Hue (\${() => hue()}°) <input type="range" min="0" max="360" oninput=\${(e) => hue.set(Number(e.target.value))} style="flex:1" /></label>
        <label style="font-size:12px;display:flex;align-items:center;gap:8px">Saturation (\${() => saturation()}%) <input type="range" min="0" max="100" oninput=\${(e) => saturation.set(Number(e.target.value))} style="flex:1" /></label>
        <label style="font-size:12px;display:flex;align-items:center;gap:8px">Shades (\${() => count()}) <input type="range" min="3" max="12" oninput=\${(e) => count.set(Number(e.target.value))} style="flex:1" /></label>
      </div>
      <div style="display:flex;gap:4px;border-radius:8px;overflow:hidden">
        \${() => generatePalette().map(p => html\`
          <div style=\${'flex:1;height:80px;background:' + p.color + ';display:flex;align-items:end;justify-content:center;padding:4px'}>
            <span style="font-size:10px;color:white;text-shadow:0 1px 2px rgba(0,0,0,0.5)">\${p.label}</span>
          </div>
        \`)}
      </div>
      <p style="font-size:11px;color:#94a3b8;margin-top:8px">HSL: \${() => hue()}° / \${() => saturation()}%</p>
    </div>
  \`;
}

mount(App(), document.getElementById('app'));` },

  { title: 'Markdown Preview', code: `import { createSignal, html, mount, raw } from 'onefold';

function App() {
  const text = createSignal('# Hello onefold\\n\\nThis is a **markdown** preview.\\n\\n- Item one\\n- Item two\\n- Item three\\n\\n> Blockquote here\\n\\nInline \\\`code\\\` and [links](https://onefoldjs.com).');

  function parseMarkdown(md) {
    return md
      .replace(/^### (.+)$/gm, '<h4>$1</h4>')
      .replace(/^## (.+)$/gm, '<h3>$1</h3>')
      .replace(/^# (.+)$/gm, '<h2>$1</h2>')
      .replace(/\\*\\*(.+?)\\*\\*/g, '<strong>$1</strong>')
      .replace(/\\*(.+?)\\*/g, '<em>$1</em>')
      .replace(/\\\`(.+?)\\\`/g, '<code style="background:#f1f5f9;padding:2px 4px;border-radius:3px">$1</code>')
      .replace(/^> (.+)$/gm, '<blockquote style="border-left:3px solid #e5e7eb;padding-left:12px;color:#666">$1</blockquote>')
      .replace(/^- (.+)$/gm, '<li>$1</li>')
      .replace(/(<li>.*<\\/li>)/s, '<ul>$1</ul>')
      .replace(/\\[(.+?)\\]\\((.+?)\\)/g, '<a href="$2">$1</a>')
      .replace(/\\n/g, '<br>');
  }

  return html\`
    <div>
      <h3>Markdown Preview</h3>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px">
        <div>
          <label style="font-size:11px;font-weight:600;text-transform:uppercase;color:#94a3b8">Editor</label>
          <textarea oninput=\${(e) => text.set(e.target.value)} style="width:100%;height:200px;font-family:monospace;font-size:12px;resize:none;margin-top:4px">\${text()}</textarea>
        </div>
        <div>
          <label style="font-size:11px;font-weight:600;text-transform:uppercase;color:#94a3b8">Preview</label>
          <div style="border:1px solid #e5e7eb;border-radius:6px;padding:12px;min-height:200px;margin-top:4px;font-size:14px;line-height:1.6">
            \${() => raw(parseMarkdown(text()))}
          </div>
        </div>
      </div>
    </div>
  \`;
}

mount(App(), document.getElementById('app'));` },
];

const styles = css`
  .pg-page { display: flex; gap: 24px; }
  .pg-sidebar {
    width: 200px;
    flex-shrink: 0;
    position: sticky;
    top: 80px;
    align-self: flex-start;
    max-height: calc(100vh - 100px);
    overflow-y: auto;
  }
  .pg-sidebar-title { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: var(--muted); margin-bottom: 8px; }
  .pg-sidebar-item {
    display: block;
    width: 100%;
    padding: 8px 12px;
    border: none;
    background: none;
    text-align: left;
    font-size: 13px;
    cursor: pointer;
    border-radius: 6px;
    color: var(--fg);
    margin-bottom: 2px;
    transition: background 0.15s;
  }
  .pg-sidebar-item:hover { background: var(--hover); }
  .pg-sidebar-item.active { background: var(--accent-bg); color: var(--accent); font-weight: 600; }
  .pg-main { flex: 1; min-width: 0; }
  @media (max-width: 768px) {
    .pg-page { flex-direction: column; }
    .pg-sidebar { width: 100%; position: static; max-height: none; display: flex; flex-wrap: wrap; gap: 4px; }
    .pg-sidebar-item { width: auto; }
  }
`;

export function PlaygroundPage(): Node {
  const activeIndex = createSignal(0);

  return html`
    <div class=${styles.scope}>
      <h1>Playground</h1>
      <p>Try onefold in the browser. Select an example or edit the code and click Run.</p>

      <div class="pg-page">
        <aside class="pg-sidebar">
          <div class="pg-sidebar-title">Examples</div>
          ${examples.map((ex, i) => html`
            <button
              class=${() => 'pg-sidebar-item' + (activeIndex() === i ? ' active' : '')}
              onclick=${() => activeIndex.set(i)}
            >${ex.title}</button>
          `)}
        </aside>
        <div class="pg-main">
          ${() => Playground(examples[activeIndex()].code, examples[activeIndex()].title, { allowNetwork: true })}
        </div>
      </div>
    </div>
  `;
}
